import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import SignUp_first_step_New from "./Signup_First_step_New";
import SignUp_second_step_New from "./Signup_Second_step_New";
import Success_signup from "./Success_signup";
import { userService } from "../../_services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Phone, Mail, Play, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Logo from "../../images/logo.png";
import SignupBg from "../../images/signup_bg.webp";
import mobile_login_bg from "../../images/signup_bg.png";
import VideoPlayBtn from "../../images/video_play_btn.png";
import SignupVideoBg from "../../images/signup_video_bg.webp";
import { cn } from "@/lib/utils";
import DiscountCard from "../common/DiscountCard";
import AppleModal from "../Consumer/AppleModal";
import * as z from 'zod';

const contactFormSchema = z.object({
    name: z.string().min(3, 'Name must be at least 3 characters long'),
    email: z.string()
        .min(1, 'Enter your email')
        .refine(val => userService.ValidEmailRegex(val), 'Invalid Email'),
    message: z.string().min(15, 'Message must be at least 15 characters long'),
});

const SignUp = () => {
    const [signupStep, setSignupStep] = useState('1st');
    const [step1Data, setStep1Data] = useState(null);
    const [faqData, setFaqData] = useState([]);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [supportEmail, setSupportEmail] = useState('support@nulifespanrx.com'); // Default fallback
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [secLinkUser, setSecLinkUser] = useState(false);
    const [redirectErr, setRedirectErr] = useState(false);

    // Wallet pass state
    const [deviceName, setDeviceName] = useState('');
    const [browserName, setBrowserName] = useState('');
    const [showApplePopup, setShowApplePopup] = useState(false);
    const [appleModalOpen, setAppleModalOpen] = useState(false);

    const getDeviceName = (userAgent) => {
        if (/Mobi|Android/i.test(userAgent)) {
            if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iPhone';
            return 'Android';
        }
        return 'Desktop';
    };

    const getBrowserName = (userAgent) => {
        if (/Firefox/i.test(userAgent)) return 'Firefox';
        if (/Chrome/i.test(userAgent)) return 'Chrome';
        if (/Safari/i.test(userAgent)) return 'Safari';
        if (/MSIE|Trident/i.test(userAgent)) return 'Internet Explorer';
        if (/Edge/i.test(userAgent)) return 'Edge';
        if (/Opera|OPR/i.test(userAgent)) return 'Opera';
        return 'Unknown';
    };

    useEffect(() => {
        // Get support email from client-side logic (no API call needed!)
        // getemail() is now synchronous and returns email based on environment
        const email = userService.getemail();
        setSupportEmail(email);

        // Detect device and browser for wallet passes
        const userAgent = window.navigator.userAgent;
        const detectedDevice = getDeviceName(userAgent);
        const detectedBrowser = getBrowserName(userAgent);
        const isSafari = /Safari/.test(userAgent) && /Version/.test(userAgent) && !/CriOS/.test(userAgent);

        setDeviceName(detectedDevice);
        setBrowserName(detectedBrowser);

        // Show Apple popup (email pass) for iPhone users NOT on Safari
        if (detectedDevice === 'iPhone' && !isSafari) {
            setShowApplePopup(true);
        }
        
        secondaryLinkVerify();
    }, []);

    const handleAppleWalletClick = () => {
        if (showApplePopup) {
            // iPhone + non-Safari: open modal to email the pass
            setAppleModalOpen(true);
        } else {
            // iPhone Safari or Desktop: open pass URL directly
            window.open('https://passes.nulifespanrx.com/passes/apple/apple_gn_20250616051852.pkpass', '_blank');
        }
    };

    const handleGoogleWalletClick = () => {
        window.open('https://pay.google.com/gp/v/save/eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJ3YWxsZXRAaG9uZXN0LXJ4LTIxNjMyMC5pYW0uZ3NlcnZpY2VhY2NvdW50LmNvbSIsImF1ZCI6Imdvb2dsZSIsIm9yaWdpbnMiOlsid3d3LmV4YW1wbGUuY29tIl0sInR5cCI6InNhdmV0b3dhbGxldCIsInBheWxvYWQiOnsiZ2VuZXJpY0NsYXNzZXMiOlt7ImlkIjoiMzM4ODAwMDAwMDAyMjExNjIwNC41NzE1NWVhZC0zYjFjLTQwMDMtYjZmMy0wMDk1NzA4NzU3NTgifV0sImdlbmVyaWNPYmplY3RzIjpbeyJjYXJkVGl0bGUiOnsiZGVmYXVsdFZhbHVlIjp7Imxhbmd1YWdlIjoiZW4tVVMiLCJ2YWx1ZSI6IlByZXNjcmlwdGlvbiBEaXNjb3VudCBjYXJkIn19LCJjbGFzc0lkIjoiMzM4ODAwMDAwMDAyMjExNjIwNC41NzE1NWVhZC0zYjFjLTQwMDMtYjZmMy0wMDk1NzA4NzU3NTgiLCJoZWFkZXIiOnsiZGVmYXVsdFZhbHVlIjp7Imxhbmd1YWdlIjoiZW4tVVMiLCJ2YWx1ZSI6Ik51TGlmZVNwYW4gUngifX0sImhleEJhY2tncm91bmRDb2xvciI6IiNmMTlmNTciLCJpZCI6IjMzODgwMDAwMDAwMjIxMTYyMDQuMzEzYTQ3YjktMGI0OS00YzY2LTljNmMtZGFhZmRjYmQ3M2NhIiwibG9nbyI6eyJjb250ZW50RGVzY3JpcHRpb24iOnsiZGVmYXVsdFZhbHVlIjp7Imxhbmd1YWdlIjoiZW4tVVMiLCJ2YWx1ZSI6Ik51TGlmZVNwYW5fTG9nbyBjYXJkIGxvZ28ifX0sInNvdXJjZVVyaSI6eyJ1cmkiOiJodHRwczovL3Bhc3Nlcy5udWxpZmVzcGFucnguY29tL0ljb25zL05MU1JYMTE0eDExNC5wbmcifX0sInN1YmhlYWRlciI6eyJkZWZhdWx0VmFsdWUiOnsibGFuZ3VhZ2UiOiJlbi1VUyIsInZhbHVlIjoiVGhpcyBjYXJkIGlzIGZyZWUuIFRoaXMgaXMgbm90IGluc3VyYW5jZS4ifX0sInRleHRNb2R1bGVzRGF0YSI6W3siYm9keSI6IjAyMzUxOCIsImhlYWRlciI6IkJJTiIsImlkIjoiYmluIn0seyJib2R5IjoiQVJYIiwiaGVhZGVyIjoiUENOIiwiaWQiOiJwY24ifSx7ImJvZHkiOiJSWEgxMjM0IiwiaGVhZGVyIjoiR3JvdXAgIyAoQWNjZXNzIENvZGUpIiwiaWQiOiJncm91cCJ9LHsiYm9keSI6IkFYMTI5MDYzIiwiaGVhZGVyIjoiTWVtYmVyIElEIiwiaWQiOiJtZW1iZXJpZCJ9XX1dfX0.FyMzF30mZXxcWfYDpTB02dwsyrnFqipZZg3uhc3PtdUQPBOQfgTNBkW78IBDyuzEJCGsZPF7i7NxqfVcFlBvoyUusM-3fIH407mN5l329qBg-QnAMjBJnnQdGlPCuEXXbDWeersGhNj1sv0EtmOmV3osv1_sRS3w27emdqUsMqRkZlJlyylt6yzt3iyQsnUy_wiaWZXxftmpDOz4ibQhxK_76biMVz0hmmvQTKjQ6E86VwnC3261k1qYGh7V22Ruy33c0WowWMKaN4m5WyVOtjEJgjd7n_USWHYt-Vm7exNEpq4tMn3-44SJKVnC9oiW1oEGpHDydx7-X_yDValVmw', '_blank');
    };

    // Build FAQ data when supportEmail is available
    useEffect(() => {
        const faqList = [
            {
                question: "Is this really FREE?",
                answer: "Yes absolutely no cost to you now or ever. Not only do other companies pay you less but they charge you an initiation and monthly fee. We think that is outrageous."
            },
            {
                question: "Why is it that agents in the NuLifeSpan Rx Distributor program make more money than anyone else in the industry?",
                answer: "Our goal was to get our discount card spread nationwide. To accomplish that, we have to pay our reps the most in the industry and our card has to provide the greatest discount. We study and research every possible way to get our reps making the most money possible. There are no secrets – you get all the information that our top reps making $10k+ per month get."
            },
            {
                question: "How much money can I make?",
                answer: "We have people making anywhere from a few hundred dollars per month to over $10k+ per month. You will earn a competitive commission anytime someone uses your card to pick up a prescription."
            },
            {
                question: "How and when do I get paid?",
                answer: "You get paid every month through PayQuicker. You can elect to get a check mailed to you, get direct deposit into your bank account, or have a personal debit card mailed to you that contains your funds. Payments are made monthly for the previous month's transactions."
            },
            {
                question: "Can I offer a doctor, pharmacist or other health care worker or pharmacy employee money or gifts to promote the NuLifeSpan Rx Program?",
                answer: "No! Absolutely not."
            },
            {
                question: "Can I get a doctor, pharmacist or other health care worker or pharmacy employee to become an affiliate of the NuLifeSpan Rx Program?",
                answer: "No! No doctor, nurse, pharmacist, pharmacy intern or tech, or any other health care employee can serve as a marketer of the NuLifeSpan Rx Program."
            },
            {
                question: "What if a doctor, pharmacist, healthcare worker or pharmacy employee asks me for payment in exchange for promoting the NuLifeSpan Rx Program?",
                answer: `Politely decline and make it clear that such arrangements are against company policy. You should report such requests to ${supportEmail}. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options: Website: www.lighthouse-services.com/rxhacker or Toll-Free Telephone: English speaking: 833-222-2698, Spanish speaking: 800-216-1288`
            },
            {
                question: "Do I have to get NuLifeSpan Rx's approval of my web site or social media posts?",
                answer: "Yes! In order for us to ensure our marketing network complies with all applicable federal, state and local regulations, we require every marketer – and every contractor of every marketer – to submit their marketing materials – which includes, for example, your web site, social media posts, and marketing emails. Failure to do so will subject your contract to immediate termination."
            },
            {
                question: "What if I forget to get pre-approval of my social media or other online posts?",
                answer: "You must immediately delete any such posts (whether inadvertent or not) and submit such inappropriately posted marketing material to NuLifeSpan Rx for review and approval."
            },
            {
                question: "What if one of my subcontractors uses marketing material that they did not submit for pre-approval?",
                answer: "If you learn that one of your contractors has failed to secure pre-approval of their marketing material, you must immediately (1) cause that contractor to cease immediately the use of any such material and (2) report the same to NuLifeSpan Rx – together with the unapproved marketing materials – for review. Any marketer – and any subcontractor of any marketer – is subject to immediate termination for failure to have your marketing material pre-approved by NuLifeSpan Rx."
            },
            {
                question: "What if a pharmacist or other pharmacy employee asks me or a member of my team for a referral fee or some other payment or gift in connection with the NuLifeSpan Rx Program?",
                answer: `You must report any such request – or even the suggestion of such a request – immediately to ${supportEmail}. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options: Website: www.lighthouse-services.com/rxhacker or Toll-Free Telephone: English speaking: 833-222-2698, Spanish speaking: 800-216-1288`
            },
            {
                question: "What if I have concerns that someone is out there distributing the NuLifeSpan Rx Program in violation of NuLifeSpan Rx's compliance standards?",
                answer: `If you have any such concerns, please contact ${supportEmail} immediately so that we can address the situation as soon as possible. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options: Website: www.lighthouse-services.com/rxhacker or Toll-Free Telephone: English speaking: 833-222-2698, Spanish speaking: 800-216-1288`
            },
            {
                question: "What if I have questions about compliance or about what marketing activities I can engage in?",
                answer: `If you have any such questions, please contact your customer success manager, who can direct your questions to the appropriate NuLifeSpan Rx professional. Of course, you can always reach out to ${supportEmail} as well.`
            }
        ];
        setFaqData(faqList);
    }, [supportEmail]);

    const secondaryLinkVerify = () => {
        let linkSuffix = window.location.href;
        if (linkSuffix.includes('/signup')) {
            let text = window.location.pathname;
            let pattern = /\/signup\/[0-9a-zA-Z]/g;
            let refpattern = /\/signup\/ref\/[0-9a-zA-Z]/g;
            try {
                if (pattern.test(text) && !refpattern.test(text)) {
                    userService.ValidateSecondLink('0', '', linkSuffix)
                        .then(apidata => {
                            if (apidata[0].statusCode === 1) {
                                setSecLinkUser(true);
                                setRedirectErr(false);
                                localStorage.setItem('ReferralURL', linkSuffix);
                            } else {
                                setSecLinkUser(false);
                                setRedirectErr(true);
                            }
                        });
                }
            } catch (error) {
                console.error('Secondary link verification error:', error);
            }
        }
    };

    const validateForm = () => {
        const result = contactFormSchema.safeParse(formData);
        const fe = result.success ? {} : result.error.flatten().fieldErrors;
        setErrors({
            name: fe.name?.[0] ?? '',
            email: fe.email?.[0] ?? '',
            message: fe.message?.[0] ?? '',
        });
        return result.success;
    };

    const handleSubmitQuestion = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            const apiData = await userService.SubmitQuestion(
                formData.name,
                formData.email,
                formData.message
            );

            if (apiData.length > 0) {
                setFormData({ name: '', email: '', message: '' });
                setSuccessMsg(apiData[0].message);
                setTimeout(() => setSuccessMsg(''), 3000);
            }
        } catch (error) {
            console.error('Submit question error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleStep1Complete = (data) => {
        setStep1Data(data);
        setSignupStep('2nd');
        scrollToTop();
    };

    const playVideo = (e) => {
        setVideoPlaying(true);
    };

    // Check if user is already logged in and redirect accordingly
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        const userType = window.atob(localStorage.getItem('_Host_Pro_type'));
        if (userType === "Admin") {
            return <Navigate to="/Admin/groups" replace />;
        } else if (userType === "consumer") {
            return <Navigate to="/consumer/Summary" replace />;
        } else {
            return <Navigate to="/summary" replace />;
        }
    }

    if (redirectErr) {
        return <Navigate to="/page-not-found" replace />;
    }

    return (
        <div className="min-h-screen bg-secondary">
            {/* Signup Form Section */}
            <div className="relative overflow-hidden">
                {/* Right side - Image: normal flow on mobile, absolute right-half on lg+ */}
                <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
                    {/* Mobile: Logo above image */}
                    <div className="lg:hidden flex justify-start px-4 pt-6 pb-3 bg-secondary">
                        <img
                            src={Logo}
                            alt="NuLifeSpan Rx Logo"
                            className="h-8 object-contain"
                        />
                    </div>
                    {/* Image */}
                    <div className="relative lg:h-full">
                        <img
                            src={SignupBg}
                            alt="Sign Up"
                            className="w-full lg:h-full object-cover object-top"
                        />
                        {/* Mobile only: heading overlaid on image */}
                        <div className="absolute inset-0 flex items-end justify-start bg-black/25 lg:hidden">
                            <h2 className="text-white text-3xl font-bold text-left px-4 pb-3 drop-shadow-lg leading-snug">
                                Sign Up today &<br />Earn Cash Every Time!
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Left side - Form: always inside max-w-6xl, left half on lg+ */}
                <div className="max-w-6xl mx-auto px-4 py-12">
                    <div className="lg:w-1/2 pr-4 lg:pr-16 pt-6 pb-10">
                        {/* Logo - desktop only */}
                        <div className="hidden lg:flex justify-start mb-4">
                            <img
                                src={Logo}
                                alt="NuLifeSpan Rx Logo"
                                className="h-8 lg:h-9 object-contain"
                            />
                        </div>
                        {/* Form steps */}
                        {signupStep === '1st' ? (
                            <SignUp_first_step_New isSecUser={secLinkUser} onNext={handleStep1Complete} />
                        ) : signupStep === '2nd' ? (
                            <SignUp_second_step_New isSecUser={secLinkUser} />
                        ) : signupStep === 'Done' ? (
                            <Success_signup />
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Video Section */}
            <div className="bg-card">
                <div className="max-w-6xl mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                        {/* Video Player */}
                        <div className="relative rounded-lg overflow-hidden aspect-video group cursor-pointer">
                            {!videoPlaying ? (
                                <>
                                    {/* Background Image */}
                                    <img 
                                        src={SignupVideoBg} 
                                        alt="Video Background" 
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    {/* Play Button Overlay */}
                                    <div
                                        onClick={playVideo}
                                        className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors"
                                    >
                                        <div className="bg-card rounded-full p-4 opacity-90 group-hover:opacity-100 transition-opacity shadow-lg">
                                            <Play className="h-10 w-10 text-accent" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <iframe
                                    className="w-full h-full"
                                    src="https://www.youtube.com/embed/RryTwk5OD_c?autoplay=1&rel=0"
                                    allowFullScreen
                                    title="Program Video"
                                    allow="autoplay"
                                ></iframe>
                            )}
                        </div>

                        {/* Video Content */}
                        <div className="space-y-4">
                        <p className="text-accent font-semibold">Watch and Learn about our program</p>
                        <h2 className="text-4xl font-bold text-foreground mb-4">
                            Helping people save money has never been so profitable
                        </h2>
                        <p className="text-xl text-foreground opacity-70">
                            Sign up now to be an affiliate and start down the path to financial freedom.
                        </p>
                        <Button variant="secondary" size="lg" onClick={scrollToTop} className="w-auto">
                            Sign up Today!
                        </Button>
                    </div>
                </div>
            </div>
            </div>

            {/* FAQ Section */}
            {!secLinkUser && (
                <div className="bg-secondary">
                    <div className="max-w-6xl mx-auto px-4 py-16">
                        <div className="text-center mb-8">
                            <h2 className="text-4xl font-bold text-foreground mb-4">
                                Frequently Asked Questions
                            </h2>
                            <p className="text-xl text-foreground opacity-70">
                                You've got questions? We've got answers!
                            </p>
                        </div>

                        <div>
                            <Accordion type="single" collapsible className="space-y-4">
                                {faqData.map((faq, index) => (
                                    <AccordionItem
                                        key={index}
                                        value={`item-${index}`}
                                        className="bg-card shadow-md rounded-lg  px-6 data-[state=open]:border-accent"
                                    >
                                        <AccordionTrigger className="text-left font-semibold hover:no-underline py-4">
                                            {faq.question}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-foreground opacity-70 pb-4">
                                            {faq.answer}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>

                        <div className="text-center mt-12">
                            <Button variant="secondary" size="lg" onClick={scrollToTop} className="w-auto">
                                Signup Today!
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Discount Cards Section */}
            <div className="bg-card py-16">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-foreground mb-4">
                            Our Discount Cards
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <DiscountCard 
                            side="front"
                            bin="023518"
                            pcn="ARX"
                            groupNumber="NLS1234"
                            memberId="AX129063"
                        />
                        <DiscountCard 
                            side="back"
                            groupNumber="NLS1234"
                            memberId="AX129063"
                            isLinked={true}
                            deviceName={deviceName}
                            browserName={browserName}
                            onAppleWalletClick={handleAppleWalletClick}
                            onGoogleWalletClick={handleGoogleWalletClick}
                        />
                    </div>
                </div>
            </div>


            {/* Apple Wallet Modal */}
            <AppleModal
                isOpen={appleModalOpen}
                onClose={() => setAppleModalOpen(false)}
                homepagePass={true}
                showApplePopup={showApplePopup}
                browserName={browserName}
            />

            {/* Contact Form Section */}
            <div className="bg-secondary py-16">
                <div className="max-w-6xl mx-auto px-4">
                    <div>
                        <div className="text-center mb-10">
                            <h2 className="text-4xl font-bold text-foreground mb-4">Still Have Questions?</h2>
                            <p className="text-xl text-foreground opacity-70">
                                Need more info? No problem! Just reach out to us using this contact form
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8 items-center">
                            {/* Left - Form (2/3) */}
                            <div className="lg:col-span-2">
                                <div className="bg-card rounded-lg shadow-md p-8">
                                    <form onSubmit={handleSubmitQuestion} className="space-y-4">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            {/* Left Column - Name and Email */}
                                            <div className="flex flex-col gap-4">
                                                {/* Name Field */}
                                                <div className="relative">
                                                    <Input
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        className={cn("peer h-12", errors.name && "border-destructive")}
                                                        disabled={isLoading}
                                                        placeholder=" "
                                                    />
                                                    <Label htmlFor="name" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                        Name
                                                    </Label>
                                                    {errors.name && <div className="min-h-[18px] mt-1">
                                                        <p className="text-sm text-destructive">{errors.name}</p>
                                                    </div>}
                                                </div>
                                                {/* Email Field */}
                                                <div className="relative">
                                                    <Input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        className={cn("peer h-12", errors.email && "border-destructive")}
                                                        disabled={isLoading}
                                                        placeholder=" "
                                                    />
                                                    <Label htmlFor="email" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                        Email
                                                    </Label>
                                                    {errors.email && <div className="min-h-[18px] mt-1">
                                                        <p className="text-sm text-destructive">{errors.email}</p>
                                                    </div>}
                                                </div>
                                            </div>

                                            {/* Right Column - Textarea: fixed height = left column total (156px) */}
                                            <div>
                                                <div className="relative">
                                                    <Textarea
                                                        id="message"
                                                        name="message"
                                                        value={formData.message}
                                                        onChange={handleInputChange}
                                                        className={cn("peer h-[110px] resize-none", errors.message && "border-destructive")}
                                                        disabled={isLoading}
                                                        placeholder=" "
                                                    />
                                                    <Label htmlFor="message" className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                        Your Message
                                                    </Label>
                                                </div>
                                                {/* Reserved slot */}
                                                <div className="min-h-[18px] mt-1">
                                                    {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
                                                </div>
                                            </div>
                                        </div>

                                        {successMsg && (
                                            <Alert className="border-green-200 bg-green-50 text-green-800">
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                <AlertDescription className="text-green-800">{successMsg}</AlertDescription>
                                            </Alert>
                                        )}

                                        <div className="flex justify-center">
                                            <Button type="submit" variant="secondary" size="lg" className="w-auto" disabled={isLoading}>
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    'Submit'
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Right - Contact Info (1/3) */}
                            <div className="lg:col-span-1 space-y-8">
                                {/* Call Us */}
                                <a href="tel:8777879372" className="flex items-center gap-4 group hover:no-underline">
                                    <div className="flex-shrink-0 w-16 h-16 rounded-full border-2 border-foreground group-hover:border-accent flex items-center justify-center transition-colors">
                                        <Phone className="h-7 w-7 text-foreground group-hover:text-accent transition-colors" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-accent text-2xl">Call us</p>
                                        <p className="text-foreground font-medium text-base group-hover:text-accent transition-colors">(945) 403-0030</p>
                                    </div>
                                </a>

                                {/* Email Us */}
                                <a href={`mailto:${supportEmail}`} className="flex items-center gap-4 group hover:no-underline">
                                    <div className="flex-shrink-0 w-16 h-16 rounded-full border-2 border-foreground group-hover:border-accent flex items-center justify-center transition-colors">
                                        <Mail className="h-7 w-7 text-foreground group-hover:text-accent transition-colors" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-accent text-2xl">Email us</p>
                                        <p className="text-foreground font-medium text-base group-hover:text-accent transition-colors break-all">{supportEmail}</p>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-secondary border-t border-border py-8">
                <div className="max-w-6xl mx-auto px-4 text-center text-sm text-foreground opacity-70">
                    <div className="mb-2">
                        <a href="/terms" className="hover:text-accent">Terms</a>
                        <span className="mx-2">|</span>
                        <a href="https://nulifespanrx.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-accent">
                            Privacy
                        </a>
                    </div>
                    <div>
                        Copyright © {new Date().getFullYear()} NuLifeSpan Rx. All rights reserved.
                    </div>
                </div>
            </footer>

            {/* Mobile background image */}
            <div className="fixed inset-0 -z-10 lg:hidden">
                <img
                    src={mobile_login_bg}
                    alt=""
                    className="w-full h-full object-cover object-top opacity-10"
                />
            </div>
        </div>
    );
};

export default SignUp;


